/**
 * Operations Monitor Page
 * Real-time operations monitor showing mission status and market capacity
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner } from '../../components/common';
import { OperationsMonitorService } from '../../services/reports/OperationsMonitorService';
import type { OperationsMonitorData, KPICard, ReportFilters } from '../../types/reports';
import {
  Card,
  CardContent,
  Select,
} from '../../components/ui';

const OperationsMonitorPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState<OperationsMonitorData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filters, setFilters] = useState<ReportFilters>({
    category: 'All Categories',
    ghostTier: 'All Tiers',
    missionType: 'All Types',
  });


  const calculateDates = (range?: string) => {
    const now = new Date();
    let startDate: Date | undefined;
    const endDate = now;

    switch (range) {
      case 'Last 24 Hours':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'Last 7 Days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'Last 30 Days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'Last 90 Days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        startDate = undefined;
    }

    return { startDate, endDate };
  };

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const { startDate, endDate } = calculateDates(filters.dateRange);
      const monitorData = await OperationsMonitorService.getOperationsMonitorData({
        ...filters,
        startDate,
        endDate,
      });
      setData(monitorData);
    } catch (error) {
      console.error('Failed to fetch operations monitor data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchData();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchData]);

  const formatLastUpdatedString = (date: Date): string => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'a few seconds ago';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    return `${Math.floor(seconds / 3600)} hours ago`;
  };

  const getTrendIcon = (trend?: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ fontSize: 14 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ fontSize: 14 }} />;
      default:
        return <RemoveIcon sx={{ fontSize: 14 }} />;
    }
  };

  const getTrendColor = (trend?: 'up' | 'down' | 'neutral', color?: string) => {
    if (color === 'error') return theme.palette.error.main;
    if (color === 'success') return theme.palette.success.main;
    if (color === 'warning') return theme.palette.warning.main;
    if (color === 'primary') return theme.palette.primary.main;

    switch (trend) {
      case 'up':
        return theme.palette.success.main;
      case 'down':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getValueColor = (color?: string) => {
    switch (color) {
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'primary':
        return theme.palette.primary.main;
      default:
        return theme.palette.text.primary;
    }
  };

  const renderKPICard = (kpi: KPICard) => (
    <Card
      sx={{
        border: `1px solid ${theme.customColors.tableBorder}`,
        height: '100%',
      }}
    >
      <CardContent>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.75rem',
            marginBottom: theme.spacing(1),
            fontWeight: 500,
          }}
        >
          {kpi.title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: getValueColor(kpi.color),
            marginBottom: theme.spacing(1),
          }}
        >
          {kpi.value}
        </Typography>
        {kpi.change && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Box sx={{ color: getTrendColor(kpi.trend, kpi.color) }}>
              {getTrendIcon(kpi.trend)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: getTrendColor(kpi.trend, kpi.color),
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              {kpi.change}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (isLoading && !data) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(2),
          }}
        >
          Operations Monitor
        </Typography>

        {/* Filters */}
        <Box
          sx={{
            display: 'flex',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
            flexWrap: 'wrap',
          }}
        >
          <Select
            size="small"
            label="Date Range"
            value={filters.dateRange || 'Last 30 Days'}
            onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="Last 24 Hours">Last 24 Hours</MenuItem>
            <MenuItem value="Last 7 Days">Last 7 Days</MenuItem>
            <MenuItem value="Last 30 Days">Last 30 Days</MenuItem>
            <MenuItem value="Last 90 Days">Last 90 Days</MenuItem>
          </Select>

          <Select
            size="small"
            label="Category"
            value={filters.category || 'All Categories'}
            onChange={(e) => setFilters({ ...filters, category: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="All Categories">All Categories</MenuItem>
            <MenuItem value="Missing Person / Pet / Wellness Check">Missing Person / Pet / Wellness Check</MenuItem>
            <MenuItem value="Background Checks">Background Checks</MenuItem>
            <MenuItem value="FMLA / Disability Abuse">FMLA / Disability Abuse</MenuItem>
            <MenuItem value="Cheating Spouse">Cheating Spouse</MenuItem>
            <MenuItem value="Child Custody">Child Custody</MenuItem>
            <MenuItem value="Criminal Investigations">Criminal Investigations</MenuItem>
          </Select>

          <Select
            size="small"
            label="Ghost Tier"
            value={filters.ghostTier || 'All Tiers'}
            onChange={(e) => setFilters({ ...filters, ghostTier: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 150 } }}
          >
            <MenuItem value="All Tiers">All Tiers</MenuItem>
            <MenuItem value="Junior">Junior</MenuItem>
            <MenuItem value="Senior">Senior</MenuItem>
            <MenuItem value="Elite">Elite</MenuItem>
          </Select>

          <Select
            size="small"
            label="Mission Type"
            value={filters.missionType || 'All Types'}
            onChange={(e) => setFilters({ ...filters, missionType: e.target.value as string })}
            formControlProps={{ sx: { minWidth: 180 } }}
          >
            <MenuItem value="All Types">All Types</MenuItem>
            <MenuItem value="QuickHit">Quick Hit</MenuItem>
            <MenuItem value="LightSurveillance">Light Surveillance</MenuItem>
            <MenuItem value="FullInvestigative">Full Investigative</MenuItem>
          </Select>
        </Box>

        {/* Status Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing(3),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <RefreshIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
              }}
            >
              Last updated: {data ? formatLastUpdatedString(data.lastUpdated) : 'loading...'}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                Auto-Refresh
              </Typography>
            }
          />
        </Box>
      </Box>

      {/* Mission Counts Section */}
      {data && (
        <>
          <Box sx={{ marginBottom: theme.spacing(4) }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(2),
                fontSize: '1rem',
              }}
            >
              Mission Counts
            </Typography>
            <Grid container spacing={2}>
              {data.missionCounts.map((kpi) => (
                <Grid item xs={12} sm={6} md={4} lg={2} key={kpi.title}>
                  {renderKPICard(kpi)}
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Market Capacity Section */}
          <Box sx={{ marginBottom: theme.spacing(2) }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                marginBottom: theme.spacing(2),
                fontSize: '1rem',
              }}
            >
              Market Capacity
            </Typography>
            <Grid container spacing={2}>
              {data.marketCapacity.map((kpi) => (
                <Grid item xs={12} sm={6} md={3} key={kpi.title}>
                  {renderKPICard(kpi)}
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Chart Sections */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  minHeight: 380,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(2),
                      fontSize: '1rem',
                    }}
                  >
                    Mission Type Trend
                  </Typography>
                  {data.missionStatusTrend ? (
                    <Box>
                      <ResponsiveContainer width="100%" height={350}>
                        <LineChart
                          data={data.missionStatusTrend}
                          margin={{ bottom: 10, left: 10, right: 10, top: 10 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.customColors.tableBorder} />
                          <XAxis
                            dataKey="date"
                            stroke={theme.palette.text.secondary}
                            style={{ fontSize: '0.75rem' }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                            interval="preserveStartEnd"
                            minTickGap={50}
                          />
                          <YAxis
                            stroke={theme.palette.text.secondary}
                            style={{ fontSize: '0.75rem' }}
                          />
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.customColors.tableBorder}`,
                              borderRadius: theme.shape.borderRadius,
                            }}
                            labelStyle={{ color: theme.palette.text.primary }}
                          />
                          <Legend wrapperStyle={{ paddingTop: '10px' }} />
                          <Line
                            type="monotone"
                            dataKey="fullInvestigative"
                            stroke={theme.palette.warning.main}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 6 }}
                            name="Full Investigative"
                          />
                          <Line
                            type="monotone"
                            dataKey="lightSurveillance"
                            stroke={theme.palette.info.main}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 6 }}
                            name="Light Surveillance"
                          />
                          <Line
                            type="monotone"
                            dataKey="quickHit"
                            stroke={theme.palette.success.main}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 6 }}
                            name="Quick Hit"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 400,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Typography variant="body2">No data available</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  minHeight: 380,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(2),
                      fontSize: '1rem',
                    }}
                  >
                    Ghost Status Distribution
                  </Typography>
                  {data.ghostStatusDistribution ? (
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={data.ghostStatusDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ status, percent }: any) => `${status}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={130}
                          innerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          nameKey="status"
                        >
                          {data.ghostStatusDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 400,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      <Typography variant="body2">No data available</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </MainLayout>
  );
};

export default OperationsMonitorPage;
